import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge } from 'react-bootstrap';
import { clientAPI, proposalAPI, quotationAPI } from '../services/api';

const Dashboard = () => {
  const [stats, setStats] = useState({
    clients: 0,
    proposals: 0,
    quotations: 0
  });
  const [recentProposals, setRecentProposals] = useState([]);
  const [recentQuotations, setRecentQuotations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [clientsRes, proposalsRes, quotationsRes] = await Promise.all([
        clientAPI.getAll(),
        proposalAPI.getAll(),
        quotationAPI.getAll()
      ]);

      setStats({
        clients: clientsRes.data.length,
        proposals: proposalsRes.data.length,
        quotations: quotationsRes.data.length
      });

      setRecentProposals(proposalsRes.data.slice(0, 5));
      setRecentQuotations(quotationsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Container fluid className="p-4">
      <h2 className="mb-4">Dashboard</h2>
      
      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="stat-card border-primary">
            <Card.Body className="d-flex align-items-center">
              <div className="bg-primary bg-opacity-10 p-3 rounded me-3">
                <i className="bi bi-people fs-3 text-primary"></i>
              </div>
              <div>
                <h6 className="text-muted mb-1">Total Clients</h6>
                <h3 className="mb-0">{stats.clients}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="stat-card border-success">
            <Card.Body className="d-flex align-items-center">
              <div className="bg-success bg-opacity-10 p-3 rounded me-3">
                <i className="bi bi-file-earmark-text fs-3 text-success"></i>
              </div>
              <div>
                <h6 className="text-muted mb-1">Total Proposals</h6>
                <h3 className="mb-0">{stats.proposals}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="stat-card border-info">
            <Card.Body className="d-flex align-items-center">
              <div className="bg-info bg-opacity-10 p-3 rounded me-3">
                <i className="bi bi-receipt fs-3 text-info"></i>
              </div>
              <div>
                <h6 className="text-muted mb-1">Total Quotations</h6>
                <h3 className="mb-0">{stats.quotations}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recent Activity */}
      <Row>
        <Col md={6} className="mb-4">
          <Card>
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <i className="bi bi-clock-history me-2"></i>
                Recent Proposals
              </h5>
            </Card.Header>
            <Card.Body className="p-0">
              <Table hover className="mb-0">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Client</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentProposals.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center text-muted py-3">
                        No proposals yet
                      </td>
                    </tr>
                  ) : (
                    recentProposals.map((proposal) => (
                      <tr key={proposal._id}>
                        <td>{proposal.projectTitle}</td>
                        <td>{proposal.clientId?.clientName || 'N/A'}</td>
                        <td>{formatDate(proposal.createdAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} className="mb-4">
          <Card>
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <i className="bi bi-clock-history me-2"></i>
                Recent Quotations
              </h5>
            </Card.Header>
            <Card.Body className="p-0">
              <Table hover className="mb-0">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Amount</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentQuotations.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center text-muted py-3">
                        No quotations yet
                      </td>
                    </tr>
                  ) : (
                    recentQuotations.map((quotation) => (
                      <tr key={quotation._id}>
                        <td>{quotation.projectTitle}</td>
                        <td>
                          <Badge bg="success">
                            {formatCurrency(quotation.grandTotal)}
                          </Badge>
                        </td>
                        <td>{formatDate(quotation.createdAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;
